import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fmtN, fmtRelative } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';
import { Pagination } from '@/components/Pagination';

type Row = {
  id: string;
  actorId: string;
  action: string;
  targetType: string | null;
  targetId: string | null;
  meta: unknown;
  ip: string | null;
  createdAt: string;
  actorEmail: string | null;
};
type Env = { rows: Row[]; page: number; pageSize: number; total: number };

export function Audit() {
  const [page, setPage] = useState(1);
  const [action, setAction] = useState('');

  const { data, isLoading } = useQuery<Env>({
    queryKey: ['/admin/api/audit', page, action],
    queryFn: () => {
      const p = new URLSearchParams({ page: String(page), pageSize: '100' });
      if (action) p.set('action', action);
      return api.get<Env>(`/admin/api/audit?${p}`);
    },
  });

  return (
    <div>
      <PageHeader
        title="Audit Log"
        subtitle={data ? `${fmtN(data.total)} entries` : 'Loading…'}
      />

      <div className="ad-card p-3 mb-3">
        <input
          className="ad-input w-full"
          placeholder='Filter by action (e.g. "users.delete")'
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setPage(1);
          }}
        />
      </div>

      <div className="ad-card overflow-hidden">
        <table className="ad-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Actor</th>
              <th>Action</th>
              <th>Target</th>
              <th>Meta</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center text-text3 py-10">
                  Loading…
                </td>
              </tr>
            ) : data && data.rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-text3 py-10">
                  No audit entries yet.
                </td>
              </tr>
            ) : (
              data?.rows.map((r) => (
                <tr key={r.id}>
                  <td className="text-text2 text-xs whitespace-nowrap">
                    {fmtRelative(r.createdAt)}
                  </td>
                  <td className="font-mono text-xs">{r.actorEmail ?? r.actorId}</td>
                  <td>
                    <code className="text-xs bg-surface2 px-2 py-0.5 rounded">{r.action}</code>
                  </td>
                  <td className="text-text2 text-xs">
                    {r.targetType ? (
                      <>
                        <span className="text-text3">{r.targetType}/</span>
                        <span className="font-mono">{r.targetId?.slice(0, 8)}…</span>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="text-text2 max-w-md">
                    {r.meta ? (
                      <pre className="text-[10px] font-mono whitespace-pre-wrap text-text3 max-h-20 overflow-y-auto">
                        {JSON.stringify(r.meta, null, 2)}
                      </pre>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="text-text3 text-xs font-mono">{r.ip ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />}
    </div>
  );
}
